/**
 * BundleBuilderHandler
 *
 * Manages UI updates for Rebuy Bundle Builder products in the cart:
 * - Detects bundle items using their DOM attributes and discount messages
 * - Hides discount input when bundles are present to prevent confusion
 * - Shows a notice that bundle discounts can't be combined with other offers
 * - Applies bundle discounts with proper price distribution across items
 * - Preserves original prices to prevent compounding discounts
 * - Handles cart updates, removals, and quantity changes
 *
 * Uses RebuyEventManager for event subscriptions and RebuyUtils for discount allocation.
 */

window.BundleBuilderHandler = (function () {
  // Private variables
  let debug = false; // Debug mode toggle
  let initialized = false; // Prevents multiple initializations
  let activeDiscountPercent = 0; // Currently applied discount percentage
  let activeBundleCount = 0; // Number of bundle items in cart
  let originalPrices = new Map(); // Stores original prices before discounts
  let initAttempts = 0; // Tracks initialization attempts
  const MAX_INIT_ATTEMPTS = 10; // Maximum attempts before giving up

  /**
   * Debug logging helper - only logs when debug mode is enabled
   */
  function log(...args) {
    if (debug) {
      console.log('[BundleBuilderHandler]', ...args);
    }
  }

  /**
   * Checks if all required Rebuy components are loaded and ready
   * @returns {boolean} True if Rebuy is ready for use
   */
  function isRebuyReady() {
    return !!(
      window.Rebuy &&
      window.Rebuy.Cart &&
      typeof window.Rebuy.Cart.getCart === 'function' &&
      window.RebuyEventManager &&
      window.RebuyUtils &&
      typeof window.RebuyUtils.allocateDiscountAcrossItems === 'function'
    );
  }

  /**
   * Detects bundle items in the cart using DOM-based approach
   * @returns {Array} Array of DOM elements that are bundle items
   */
  function detectBundleItems() {
    // Find all cart items
    const allCartItems = document.querySelectorAll('.rebuy-cart__flyout-item');

    // Filter to bundle items (two detection methods)
    const bundleItems = Array.from(allCartItems).filter(item => {
      // Method 1: Check for bundle builder attribution in class name
      const hasAttributionClass = item.className.includes(
        'property-_attribution:rebuy-bundle-builder'
      );

      // Method 2: Check for discount message that indicates a bundle
      const discountMessage = item.querySelector('.rebuy-cart__flyout-item-discount-message');
      const hasDiscountMessage =
        discountMessage?.textContent.includes('Rabatt angewendet') || false;

      return hasAttributionClass || hasDiscountMessage;
    });

    log(`Detected ${bundleItems.length} bundle items`);
    return bundleItems;
  }

  /**
   * Extracts discount percentage from bundle items or uses count-based logic as fallback
   * @param {Array} bundleItems - DOM elements of bundle items
   * @returns {number} Discount percentage to apply
   */
  function calculateBundleDiscount(bundleItems) {
    if (!bundleItems.length) return 0;

    // Try to extract discount from message (most accurate)
    for (const item of bundleItems) {
      const discountMessage = item.querySelector('.rebuy-cart__flyout-item-discount-message');
      if (discountMessage) {
        const match = discountMessage.textContent.match(/(\d+)%/);
        if (match) {
          const percent = parseInt(match[1], 10);
          log(`Extracted ${percent}% discount from message: "${discountMessage.textContent}"`);
          return percent;
        }
      }
    }

    // Fallback to count-based logic if message extraction fails
    const bundleCount = bundleItems.length;
    log(`Using count-based discount for ${bundleCount} items`);

    // Apply tiered discounts based on number of items
    if (bundleCount >= 6) return 20;
    if (bundleCount >= 4) return 15;
    if (bundleCount >= 3) return 10;
    if (bundleCount >= 2) return 5;

    return 0;
  }

  /**
   * Resets all prices to their original state (before discounting)
   */
  function resetBundlePrices() {
    originalPrices.forEach((originalPrice, itemId) => {
      const item = document.querySelector(`[data-item-id="${itemId}"]`);
      if (!item) return;

      const priceContainer = item.querySelector('.rebuy-cart__flyout-item-price');
      if (!priceContainer) return;

      // Restore original price display
      priceContainer.innerHTML = `
        <span class="rebuy-money">
          <span class="sr-only">Price</span>
          <span>€ ${originalPrice}</span>
        </span>
      `;
    });

    log('Reset prices for bundle items');
  }

  /**
   * Captures original prices from DOM elements
   * Only captures each price once to prevent referencing already-discounted prices
   * @param {Array} bundleItems - DOM elements of bundle items
   */
  function captureOriginalPrices(bundleItems) {
    bundleItems.forEach(item => {
      const itemId = item.getAttribute('data-item-id');
      // Only capture if we don't already have this price
      if (!originalPrices.has(itemId)) {
        // Strategy: Look for either compare-at price (if discount already applied)
        // or regular price (if no discount yet)
        const compareAtPrice = item.querySelector(
          '.rebuy-cart__flyout-item-price .rebuy-money.compare-at span:last-child'
        );
        const regularPrice = item.querySelector(
          '.rebuy-cart__flyout-item-price .rebuy-money span:last-child'
        );

        if (compareAtPrice) {
          // If there's a compare-at price, use that as the original
          const price = compareAtPrice.textContent.trim().replace('€', '').trim();
          originalPrices.set(itemId, price);
          log(`Stored original price from compare-at for item ${itemId}: ${price}`);
        } else if (regularPrice) {
          // Otherwise use the regular price
          const price = regularPrice.textContent.trim().replace('€', '').trim();
          originalPrices.set(itemId, price);
          log(`Stored original price from regular for item ${itemId}: ${price}`);
        }
      }
    });
  }

  /**
   * Converts DOM items to cart-item-like objects for discount allocation
   * @param {Array} bundleItems - DOM elements of bundle items
   * @returns {Array} Array of objects compatible with allocateDiscountAcrossItems
   */
  function prepareCartItems(bundleItems) {
    const cartItems = [];

    for (const item of bundleItems) {
      const itemId = item.getAttribute('data-item-id');

      // Always use the original price to prevent compounding discounts
      if (!originalPrices.has(itemId)) {
        log(`No original price stored for item ${itemId}, cannot prepare cart item`);
        continue;
      }

      const originalPrice = originalPrices.get(itemId);

      // Parse quantity from the DOM
      const quantityElement = item.querySelector('.rebuy-cart__flyout-item-quantity-widget-label');
      let quantity = 1;
      if (quantityElement) {
        const qtyText = quantityElement.textContent.trim();
        const qtyMatch = qtyText.match(/\d+/);
        if (qtyMatch) {
          quantity = parseInt(qtyMatch[0], 10);
        }
      }

      // Convert to cents for the allocator (Shopify standard)
      const priceEuro = parseFloat(originalPrice.replace(',', '.'));
      const priceCents = Math.round(priceEuro * 100);

      cartItems.push({
        id: itemId,
        price: priceCents.toString(), // Shopify stores price as string
        quantity: quantity,
      });
    }

    log(`Prepared ${cartItems.length} cart items for allocation`);
    return cartItems;
  }

  /**
   * Updates item prices with discounts using allocation for penny-perfect distribution
   * @param {Array} bundleItems - DOM elements of bundle items
   * @param {number} discountPercent - Discount percentage to apply
   */
  function updateItemPrices(bundleItems, discountPercent) {
    if (!bundleItems.length || discountPercent <= 0) return;

    log(`Updating prices for ${bundleItems.length} items with ${discountPercent}% discount`);

    // First capture original prices if we don't have them yet
    captureOriginalPrices(bundleItems);

    try {
      // Prepare cart-like items for allocation using ORIGINAL prices
      const cartItems = prepareCartItems(bundleItems);

      if (cartItems.length === 0) {
        log('No cart items could be prepared, cannot update prices');
        return;
      }

      // Use RebuyUtils to allocate discount properly with penny-perfect distribution
      const { allocations } = window.RebuyUtils.allocateDiscountAcrossItems(
        cartItems,
        discountPercent
      );

      // Update UI with allocated discounts
      bundleItems.forEach(item => {
        const itemId = item.getAttribute('data-item-id');
        const priceContainer = item.querySelector('.rebuy-cart__flyout-item-price');
        if (!priceContainer || !itemId || !allocations.has(itemId)) return;

        const allocation = allocations.get(itemId);
        const originalPrice = originalPrices.get(itemId);

        if (!originalPrice) {
          log(`Missing original price for item ${itemId}, skipping update`);
          return;
        }

        // Format with comma as decimal separator for European markets
        const discountedPrice = allocation.discountedPrice.toFixed(2).replace('.', ',');

        // Update the display
        priceContainer.innerHTML = `
          <div>
            <span class="rebuy-money sale">
              <span class="sr-only">Discounted Price</span>
              <span>€ ${discountedPrice}</span>
            </span>
            <span class="rebuy-money compare-at">
              <span class="sr-only">Original Price</span>
              <span>€ ${originalPrice}</span>
            </span>
          </div>
        `;

        log(`Updated price for item ${itemId}: ${originalPrice} -> ${discountedPrice}`);
      });
    } catch (error) {
      console.error('[BundleBuilderHandler] Error in discount allocation:', error);

      // Fallback to simple percentage discount if allocation fails
      // Not as precise but better than no discount
      bundleItems.forEach(item => {
        const itemId = item.getAttribute('data-item-id');
        if (!originalPrices.has(itemId)) return;

        const priceContainer = item.querySelector('.rebuy-cart__flyout-item-price');
        if (!priceContainer) return;

        const originalPrice = originalPrices.get(itemId);

        // Convert to number, being careful about comma as decimal separator
        const price = parseFloat(originalPrice.replace(',', '.'));

        // Calculate discounted price with simple percentage
        const discountedPrice = (price * (1 - discountPercent / 100)).toFixed(2).replace('.', ',');

        // Update the display
        priceContainer.innerHTML = `
          <div>
            <span class="rebuy-money sale">
              <span class="sr-only">Discounted Price</span>
              <span>€ ${discountedPrice}</span>
            </span>
            <span class="rebuy-money compare-at">
              <span class="sr-only">Original Price</span>
              <span>€ ${originalPrice}</span>
            </span>
          </div>
        `;

        log(`Fallback: Updated price for item ${itemId}: ${originalPrice} -> ${discountedPrice}`);
      });
    }
  }

  /**
   * Manages bundle discount notice - shows when bundles present, hides otherwise
   * @param {boolean} hasBundle - Whether bundle items are present
   */
  function manageBundleNotice(hasBundle) {
    if (hasBundle) {
      // Check if notice already exists to avoid duplicates
      if (!document.getElementById('bundle-discount-notice')) {
        // Create the notice
        const notice = document.createElement('p');
        notice.id = 'bundle-discount-notice';

        // Base styles
        notice.style.fontSize = '12px';
        notice.style.alignSelf = 'center';
        notice.style.fontStyle = 'italic';
        notice.style.textAlign = 'center';
        notice.style.color = '#666';
        notice.style.padding = '0px 8px';
        notice.textContent =
          'Los descuentos del set personalizado no se pueden combinar con otras promociones o códigos de descuento.';

        // Add mobile-specific styles using a <style> tag
        if (!document.getElementById('bundle-notice-styles')) {
          const styleTag = document.createElement('style');
          styleTag.id = 'bundle-notice-styles';
          styleTag.textContent = `
          @media (max-width: 767px) {
            #bundle-discount-notice {
              font-size: 10px !important;
              line-height: normal !important;
              padding: 0px 10px !important;
            }
          }
        `;
          document.head.appendChild(styleTag);
          log('Added responsive styles for bundle notice');
        }

        // Find the footer and prepend
        const footer = document.querySelector('[data-rebuy-cart-anchor="footer"]');
        if (footer) {
          footer.insertBefore(notice, footer.firstChild);
          log('Added bundle discount notice');
        }
      }
    } else {
      // Remove the notice if no bundles
      const notice = document.getElementById('bundle-discount-notice');
      if (notice && notice.parentNode) {
        notice.parentNode.removeChild(notice);
        log('Removed bundle discount notice');
      }
    }
  }

  /**
   * Main function to process cart and update UI accordingly
   * @param {Object} cart - Rebuy cart object
   */
  function processCart(cart) {
    // Focus on DOM-based detection which works reliably
    const bundleItems = detectBundleItems();
    const hasBundle = bundleItems.length > 0;
    const bundleCount = bundleItems.length;

    // Handle discount input visibility
    const discountInputWrapper = document.querySelector('[data-rebuy-component="discount-input"]');
    if (discountInputWrapper) {
      discountInputWrapper.style.display = hasBundle ? 'none' : '';
      log(`Discount input visibility set to: ${hasBundle ? 'hidden' : 'visible'}`);
    }

    // Handle the bundle notice
    manageBundleNotice(hasBundle);

    // Check if bundle count has changed
    const bundleCountChanged = bundleCount !== activeBundleCount;

    // Calculate and apply bundle discount if bundles are present
    if (hasBundle) {
      const discountPercent = calculateBundleDiscount(bundleItems);

      // Update prices if anything has changed or to ensure consistency
      if (bundleCountChanged || discountPercent !== activeDiscountPercent) {
        log(
          `Bundle state changed: count=${bundleCount} (was ${activeBundleCount}), discount=${discountPercent}% (was ${activeDiscountPercent}%)`
        );
        updateItemPrices(bundleItems, discountPercent);
        activeDiscountPercent = discountPercent;
        activeBundleCount = bundleCount;
      } else {
        // Even if nothing changed, ensure prices are updated
        // This handles the case where removing an item doesn't change the tier
        log('Bundle state unchanged, ensuring prices are updated');
        updateItemPrices(bundleItems, discountPercent);
      }
    } else if (activeDiscountPercent > 0 || activeBundleCount > 0) {
      // Reset if no bundles but we previously had discounts
      log('No bundles detected, resetting prices');
      resetBundlePrices();
      activeDiscountPercent = 0;
      activeBundleCount = 0;
    }
  }

  /**
   * Wrapper for processCart that includes error handling
   * @param {Object} cart - Rebuy cart object
   */
  function safeProcessCart(cart) {
    try {
      processCart(cart);
    } catch (error) {
      console.error('[BundleBuilderHandler] Error processing cart:', error);
    }
  }

  /**
   * Sets up event listeners using RebuyEventManager
   * @returns {boolean} Success status
   */
  function setupEventListeners() {
    if (!window.RebuyEventManager) {
      console.error('[BundleBuilderHandler] ERROR: RebuyEventManager is not available!');
      return false;
    }

    // Register for all relevant cart events
    window.RebuyEventManager.on('cart:change', data => {
      log('cart:change event received');
      setTimeout(() => safeProcessCart(data.cart), 100);
    })
      .on('cart:ready', data => {
        log('cart:ready event received');
        setTimeout(() => safeProcessCart(data.cart), 100);
      })
      .on('cart:show', data => {
        log('cart:show event received');
        // Run immediately and once with delay to handle race conditions
        safeProcessCart(data.cart);
        setTimeout(() => safeProcessCart(data.cart), 300);
      });

    log('Event listeners registered successfully');
    return true;
  }

  /**
   * Clears all saved state and removes UI elements
   */
  function clearState() {
    originalPrices.clear();
    activeDiscountPercent = 0;
    activeBundleCount = 0;
    log('Cleared all saved state');

    // Remove the notice if it exists
    const notice = document.getElementById('bundle-discount-notice');
    if (notice && notice.parentNode) {
      notice.parentNode.removeChild(notice);
      log('Removed bundle discount notice during reset');
    }
    // Remove the style tag if it exists
    const styleTag = document.getElementById('bundle-notice-styles');
    if (styleTag && styleTag.parentNode) {
      styleTag.parentNode.removeChild(styleTag);
      log('Removed bundle notice styles during reset');
    }
  }

  /**
   * Attempts initialization with retries
   * @returns {boolean} Success status
   */
  function attemptInitialization() {
    if (initialized) return true;

    initAttempts++;
    log(`Initialization attempt ${initAttempts}/${MAX_INIT_ATTEMPTS}`);

    // Check if Rebuy is ready
    if (!isRebuyReady()) {
      if (initAttempts < MAX_INIT_ATTEMPTS) {
        // Try again after a delay
        log('Rebuy not fully loaded yet, will retry initialization');
        setTimeout(attemptInitialization, 500);
        return false;
      } else {
        console.error(
          '[BundleBuilderHandler] Failed to initialize: Rebuy not available after maximum attempts'
        );
        return false;
      }
    }

    // Setup event listeners and mark as initialized
    if (setupEventListeners()) {
      initialized = true;
      log('Successfully initialized');

      // Initial processing if cart is already visible
      try {
        window.Rebuy.Cart.getCart().then(safeProcessCart);
      } catch (error) {
        log('Error getting initial cart, will rely on events');
      }

      return true;
    }

    return false;
  }

  // Public API
  return {
    /**
     * Initializes the handler
     * @returns {Object} this - For chainable calls
     */
    init: function () {
      if (initialized) {
        log('Already initialized');
        return this;
      }

      log('Starting initialization sequence');
      attemptInitialization();

      return this;
    },

    /**
     * Enables or disables debug logging
     * @param {boolean} enabled - Whether to enable debug logging
     * @returns {Object} this - For chainable calls
     */
    debug: function (enabled) {
      debug = enabled;
      log('Debug logging', enabled ? 'enabled' : 'disabled');
      return this;
    },

    /**
     * Forces a refresh of the cart processing
     * @returns {Object} this - For chainable calls
     */
    refresh: function () {
      if (!initialized) {
        log('Cannot refresh, not initialized');
        return this;
      }

      try {
        if (window.Rebuy && window.Rebuy.Cart) {
          window.Rebuy.Cart.getCart().then(safeProcessCart);
        }
      } catch (error) {
        console.error('[BundleBuilderHandler] Error refreshing:', error);
      }

      return this;
    },

    /**
     * For testing: Forces an update of bundle items
     * @returns {boolean} Success status
     */
    forceUpdate: function () {
      log('Forcing update of all bundle items');
      const bundleItems = detectBundleItems();
      if (bundleItems.length) {
        const discountPercent = calculateBundleDiscount(bundleItems);
        updateItemPrices(bundleItems, discountPercent);
        return true;
      }
      return false;
    },

    /**
     * Resets all stored state
     * @returns {Object} this - For chainable calls
     */
    reset: function () {
      clearState();
      return this;
    },

    /**
     * For debugging: Returns all stored original prices
     * @returns {Object} Original prices as a plain object
     */
    getOriginalPrices: function () {
      return Object.fromEntries(originalPrices);
    },
  };
})();
